<template>
  <!-- Head tags are managed imperatively so prerendered HTML is updated, not duplicated. -->
  <span hidden aria-hidden="true"></span>
</template>

<script>
const SITE_URL = "https://transpond.io";
const MANAGED_ATTR = "data-mpz-meta";

function upsertMeta(attr, key, content) {
  if (typeof document === "undefined") {
    return;
  }

  const selector = `meta[${attr}="${key}"]`;
  const existing = [...document.head.querySelectorAll(selector)];
  let target =
    existing.find((el) => el.hasAttribute(MANAGED_ATTR)) || existing[0];

  if (!target) {
    target = document.createElement("meta");
    target.setAttribute(attr, key);
    document.head.appendChild(target);
  }

  target.setAttribute(MANAGED_ATTR, "");
  target.setAttribute("content", content || "");

  existing.forEach((el) => {
    if (el !== target) {
      el.remove();
    }
  });
}

function removeMeta(attr, key) {
  if (typeof document === "undefined") {
    return;
  }

  document.head
    .querySelectorAll(`meta[${attr}="${key}"]`)
    .forEach((el) => el.remove());
}

function syncTitle(title) {
  if (typeof document === "undefined") {
    return;
  }

  document.title = title || "";

  const titles = [...document.head.querySelectorAll("title")];

  if (titles.length === 0 && title) {
    const titleEl = document.createElement("title");
    titleEl.textContent = title;
    document.head.appendChild(titleEl);
    return;
  }

  titles.forEach((el, index) => {
    if (index === 0) {
      el.textContent = title || "";
    } else {
      el.remove();
    }
  });
}

function markPrerenderReady(isReady) {
  if (typeof document === "undefined") {
    return;
  }

  if (isReady) {
    document.documentElement.setAttribute("data-prerender-ready", "true");
    document.dispatchEvent(new Event("prerender-ready"));
  } else {
    document.documentElement.removeAttribute("data-prerender-ready");
  }
}

export default {
  name: "MetaInfo",
  props: {
    title: String,
    description: String,
    noindex: String,
    image: String,
  },
  computed: {
    ogImage() {
      const image = this.image || `${SITE_URL}/icon-512x512.png`;
      if (image.startsWith("/")) return `${SITE_URL}${image}`;
      return image;
    },
    ogUrl() {
      if (this.$route) {
        const path = this.$route.path || "/";

        if (path === "/") {
          return SITE_URL;
        }

        return `${SITE_URL}${path}`;
      }

      if (typeof window !== "undefined") {
        return window.location.href;
      }

      return SITE_URL;
    },
  },
  watch: {
    title: "syncHead",
    description: "syncHead",
    noindex: "syncHead",
    image: "syncHead",
    ogUrl: "syncHead",
  },
  mounted() {
    this.syncHead();
  },
  methods: {
    syncHead() {
      if (typeof document === "undefined") {
        return;
      }

      const title = this.title || "";

      if (!title) {
        markPrerenderReady(false);
        return;
      }

      syncTitle(title);
      upsertMeta("name", "description", this.description || "");
      upsertMeta("property", "og:title", title);
      upsertMeta("property", "og:description", this.description || "");
      upsertMeta("property", "og:type", "website");
      upsertMeta("property", "og:image", this.ogImage);
      upsertMeta("property", "og:url", this.ogUrl);
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:title", title);
      upsertMeta("name", "twitter:description", this.description || "");
      upsertMeta("name", "twitter:image", this.ogImage);

      if (this.noindex) {
        upsertMeta("name", "robots", this.noindex);
      } else {
        removeMeta("name", "robots");
      }

      markPrerenderReady(true);
    },
  },
};
</script>
